import React, { Component } from 'react';
import mixpanel, { initMixpanel } from '../utils/mixpanelConsent';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';

import AuthService from './AuthService';
import Layout from './Layout';
import AuthModalContext from '../contexts/AuthModalContext';
import './DeepDescription.css';

initMixpanel();

class DeepDescription extends Component {
  static contextType = AuthModalContext;

  constructor() {
    super();
    this.state = { query: '', showScrollTop: false };
    this.Auth = new AuthService();
    this.handleCTA = this.handleCTA.bind(this);
    this.handleBrowse = this.handleBrowse.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  componentDidMount() {
    mixpanel.track('Description page');

    // Scroll-reveal via IntersectionObserver
    var els = document.querySelectorAll('.hiw-reveal');
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('hiw-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });
      els.forEach(function(el) { observer.observe(el); });
      this._observer = observer;
    } else {
      // Fallback: show everything
      els.forEach(function(el) { el.classList.add('hiw-visible'); });
    }

    window.addEventListener('scroll', this.handleScroll);

    // Hash anchor scroll — delay to let layout settle after reveal animations
    if (window.location.hash) {
      var hash = window.location.hash;
      setTimeout(function() {
        var target = document.querySelector(hash);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }

  componentWillUnmount() {
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    var showScrollTop = window.scrollY > 400;
    if (showScrollTop !== this.state.showScrollTop) {
      this.setState({ showScrollTop: showScrollTop });
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  handleCTA() {
    if (this.Auth.getToken()) {
      this.props.history.push('/sell');
    } else {
      this.context.openAuthModal('signup');
    }
  }

  handleBrowse() {
    this.props.history.push('/browse');
  }

  renderPricingCircle(value, label, color, delay) {
    var r = 42;
    var c = 2 * Math.PI * r; // ~263.9
    return (
      <div className={'tw-text-center hiw-reveal hiw-scale-in hiw-delay-' + delay}>
        <div className="tw-relative tw-w-24 tw-h-24 tw-mx-auto tw-mb-3">
          <svg className="tw-w-full tw-h-full" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={r} fill="none" stroke="#e8e8e8" strokeWidth="3" />
            <circle
              cx="50" cy="50" r={r} fill="none"
              stroke={color} strokeWidth="3" strokeLinecap="round"
              className="hiw-circle-draw"
              style={{ strokeDasharray: c, strokeDashoffset: c, transformOrigin: 'center', transform: 'rotate(-90deg)' }}
            />
          </svg>
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <span className="tw-text-xl tw-font-bold" style={{ color: color }}>{value}</span>
          </div>
        </div>
        <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#999] tw-font-medium tw-mb-0">{label}</p>
      </div>
    );
  }

  render() {
    var card = 'tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-2xl tw-border tw-border-solid tw-border-[#e8e8e8] dark:tw-border-[#2a2a2a]';
    var stepBase = card + ' tw-p-6 tw-text-center hiw-step-card';
    var iconCircle = 'tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 hiw-icon-pulse';
    var gold = '#c9a84c';
    var green = '#43a047';

    return (
      <Layout>
        <Helmet>
          <title>How It Works - Primal Market</title>
          <meta name="description" content="Learn how to buy and sell on Primal Market. Low fees, buyer protection, easy shipping, and multiple payment options." />
          <meta property="og:title" content="How It Works - Primal Market" />
          <meta property="og:description" content="Learn how to buy and sell on Primal Market. Low fees, buyer protection, easy shipping, and multiple payment options." />
        </Helmet>

        <div className="tw-max-w-5xl tw-mx-auto tw-px-4 tw-pt-16 tw-pb-8">

          {/* ==================== HERO ==================== */}
          <div className="tw-text-center tw-mb-20 hiw-reveal hiw-fade-up">
            <h1 className="tw-text-4xl md:tw-text-5xl tw-font-extrabold tw-text-[#111] dark:tw-text-white tw-mb-5 tw-leading-tight">
              How Primal Market Works
            </h1>
            <p className="tw-text-lg tw-text-[#666] dark:tw-text-[#999] tw-max-w-2xl tw-mx-auto tw-mb-10 tw-leading-relaxed">
              The marketplace built for Primal TCG. Buy, sell, and share cards with low fees and built-in buyer protection.
            </p>
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-flex-wrap">
              <button
                onClick={this.handleBrowse}
                className="tw-px-8 tw-py-3.5 tw-rounded-xl tw-font-bold tw-text-white tw-border-0 tw-cursor-pointer tw-text-base hiw-cta-btn"
                style={{ backgroundColor: gold }}
              >
                Browse Cards
              </button>
              <button
                onClick={this.handleCTA}
                className="tw-px-8 tw-py-3.5 tw-rounded-xl tw-font-bold tw-border-2 tw-border-solid tw-cursor-pointer tw-text-base tw-bg-transparent hiw-ghost-btn"
                style={{ color: gold, borderColor: gold }}
              >
                Start Selling
              </button>
            </div>
          </div>

          {/* ==================== FOR BUYERS ==================== */}
          <div className="tw-mb-20">
            <div className="hiw-reveal hiw-fade-up">
              <p className="tw-text-xs tw-font-bold tw-tracking-[0.2em] tw-uppercase tw-text-center tw-mb-2" style={{ color: gold }}>FOR BUYERS</p>
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-10">
                Find and buy cards in minutes
              </h2>
            </div>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-5">
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-1'}>
                <div className={iconCircle} style={{ backgroundColor: '#faf5e8' }}>
                  <i className="fa fa-search tw-text-lg" style={{ color: gold }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 1</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Browse &amp; Search</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  Search by card name, browse by set, or explore sellers' shops. Filter by condition, rarity, and price to find exactly what you need. Each card page shows real market prices based on recent sales so you know you're getting a fair deal.
                </p>
              </div>
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-2'}>
                <div className={iconCircle} style={{ backgroundColor: '#faf5e8' }}>
                  <i className="fa fa-credit-card tw-text-lg" style={{ color: gold }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 2</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Buy Securely</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  Pay the listed price or make an offer on eligible listings. Orders $10+ ship free with tracking included at $50+. Pay securely with Stripe or PayPal — your payment is held until you confirm delivery.
                </p>
              </div>
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-3'}>
                <div className={iconCircle} style={{ backgroundColor: '#e8f5e9' }}>
                  <i className="fa fa-check-circle tw-text-lg" style={{ color: green }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 3</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Receive &amp; Confirm</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  Once your card arrives, confirm receipt to release payment to the seller. If something's wrong, open a dispute and we'll help.
                </p>
              </div>
            </div>
          </div>

          <div className="hiw-divider tw-my-16"></div>

          {/* ==================== FOR SELLERS ==================== */}
          <div id="for-sellers" className="tw-mb-20" style={{ scrollMarginTop: '120px' }}>
            <div className="hiw-reveal hiw-fade-up">
              <p className="tw-text-xs tw-font-bold tw-tracking-[0.2em] tw-uppercase tw-text-center tw-mb-2" style={{ color: gold }}>FOR SELLERS</p>
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-10">
                List your cards and get paid
              </h2>
            </div>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-5">
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-1'}>
                <div className={iconCircle} style={{ backgroundColor: '#faf5e8' }}>
                  <i className="fa fa-camera tw-text-lg" style={{ color: gold }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 1</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">List Your Card</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  Every listing includes a catalog image automatically. You can also upload up to 4 of your own photos. Set your price, shipping cost, and condition. Choose whether to accept offers — then post.
                </p>
              </div>
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-2'}>
                <div className={iconCircle} style={{ backgroundColor: '#faf5e8' }}>
                  <i className="fa fa-truck tw-text-lg" style={{ color: gold }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 2</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Ship It</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  When someone buys your card, ship it out. Use built-in messaging to coordinate with buyers if needed. Orders $50+ require tracking. Smaller orders can ship in a plain envelope, or the buyer can add tracking for +$5.50. <Link to="/shipping" style={{ color: gold, textDecoration: 'none', fontWeight: 600 }}>See our shipping guide</Link>.
                </p>
              </div>
              <div className={stepBase + ' hiw-reveal hiw-fade-up hiw-delay-3'}>
                <div className={iconCircle} style={{ backgroundColor: '#e8f5e9' }}>
                  <i className="fa fa-dollar tw-text-lg" style={{ color: green }}></i>
                </div>
                <p className="tw-text-[10px] tw-font-bold tw-tracking-[0.15em] tw-mb-2" style={{ color: gold }}>STEP 3</p>
                <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Get Paid</h3>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                  Once the buyer confirms receipt (or after 14 days), your payout is released via Stripe or PayPal. You keep 90% + shipping.
                </p>
              </div>
            </div>
          </div>

          <div className="hiw-divider tw-my-16"></div>

          {/* ==================== BUYER PROTECTION ==================== */}
          <div id="buyer-protection" className="tw-mb-20" style={{ scrollMarginTop: '80px' }}>
            <div className="hiw-reveal hiw-fade-up">
              <p className="tw-text-xs tw-font-bold tw-tracking-[0.2em] tw-uppercase tw-text-center tw-mb-2" style={{ color: gold }}>SECURITY</p>
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-3">
                Buyer Protection
              </h2>
              <p className="tw-text-base tw-text-[#666] dark:tw-text-[#999] tw-text-center tw-max-w-lg tw-mx-auto tw-mb-10">
                Every purchase is backed by our escrow system. Your money is held safely until you confirm the item arrived as described.
              </p>
            </div>

            {/* Escrow flow */}
            <div className={'tw-rounded-2xl tw-p-8 md:tw-p-10 tw-mb-5 tw-border tw-border-solid tw-border-[#e8e8e8] dark:tw-border-[#2a2a2a] hiw-escrow-flow hiw-reveal hiw-fade-up'}>
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-4 md:tw-gap-8 tw-flex-wrap">
                <div className="tw-text-center">
                  <div className="tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3 hiw-icon-pulse" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-lock tw-text-lg" style={{ color: gold }}></i>
                  </div>
                  <p className="tw-text-xs tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-0.5">Payment Held</p>
                  <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">Funds secured in escrow</p>
                </div>
                <span className="hiw-arrow-bounce"><i className="fa fa-arrow-right tw-text-[#ccc] dark:tw-text-[#555]"></i></span>
                <div className="tw-text-center">
                  <div className="tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3 hiw-icon-pulse" style={{ backgroundColor: '#e3f2fd' }}>
                    <i className="fa fa-truck tw-text-lg" style={{ color: '#1976d2' }}></i>
                  </div>
                  <p className="tw-text-xs tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-0.5">Seller Ships</p>
                  <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">Tracking number added</p>
                </div>
                <span className="hiw-arrow-bounce" style={{ animationDelay: '0.3s' }}><i className="fa fa-arrow-right tw-text-[#ccc] dark:tw-text-[#555]"></i></span>
                <div className="tw-text-center">
                  <div className="tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3 hiw-icon-pulse" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-lg" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-xs tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-0.5">Buyer Confirms</p>
                  <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">Or auto-releases in 14 days</p>
                </div>
                <span className="hiw-arrow-bounce" style={{ animationDelay: '0.6s' }}><i className="fa fa-arrow-right tw-text-[#ccc] dark:tw-text-[#555]"></i></span>
                <div className="tw-text-center">
                  <div className="tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3 hiw-icon-pulse" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-money tw-text-lg" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-xs tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-0.5">Seller Paid</p>
                  <p className="tw-text-[10px] tw-text-[#999] tw-mb-0">Payout via Stripe or PayPal</p>
                </div>
              </div>
            </div>

            {/* Dispute + Refund cards */}
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-slide-left hiw-delay-1'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-balance-scale" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Dispute Resolution</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      If your item doesn't arrive as described, you can open a dispute directly from your orders page. We review both sides and make a fair decision.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-slide-right hiw-delay-2'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-exchange" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Easy Refunds</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      If a dispute is resolved in the buyer's favor, the payment is refunded in full. Sellers are protected too — clear communication and tracking prevent false claims.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw-divider tw-my-16"></div>

          {/* ==================== PRICING ==================== */}
          <div className="tw-mb-20">
            <div className="hiw-reveal hiw-fade-up">
              <p className="tw-text-xs tw-font-bold tw-tracking-[0.2em] tw-uppercase tw-text-center tw-mb-2" style={{ color: gold }}>PRICING</p>
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-3">
                Simple, transparent fees
              </h2>
              <p className="tw-text-base tw-text-[#666] dark:tw-text-[#999] tw-text-center tw-max-w-lg tw-mx-auto tw-mb-10">
                No hidden costs. No monthly subscriptions. You only pay when you make a sale.
              </p>
            </div>

            <div className={card + ' tw-p-8 md:tw-p-10 tw-mb-5'}>
              {/* Animated SVG circles */}
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-10 md:tw-gap-16 tw-mb-10 hiw-reveal hiw-fade-in">
                {this.renderPricingCircle('$0', 'To List', gold, 1)}
                {this.renderPricingCircle('10%', 'On Sale Price', gold, 2)}
                {this.renderPricingCircle('100%', 'Keep Shipping', gold, 3)}
              </div>

              {/* Checklist */}
              <div className="tw-space-y-4 hiw-reveal hiw-fade-up hiw-delay-3">
                <div className="tw-flex tw-items-start tw-gap-3 hiw-check-item">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-[10px]" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-leading-relaxed tw-mb-0">
                    <strong className="tw-text-[#111] dark:tw-text-white">Free to list</strong> — Post as many cards as you want. No listing fees, no monthly charges.
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 hiw-check-item">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-[10px]" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-leading-relaxed tw-mb-0">
                    <strong className="tw-text-[#111] dark:tw-text-white">You keep 90%</strong> — We only take 10% of the item price when you make a sale. On orders under $9, a small processing fee may apply. Buyers cover checkout processing — not you.
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 hiw-check-item">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-[10px]" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-leading-relaxed tw-mb-0">
                    <strong className="tw-text-[#111] dark:tw-text-white">Keep all shipping</strong> — The fee is calculated on the item price only. Your shipping cost is passed through to you in full.
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 hiw-check-item">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-[10px]" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-leading-relaxed tw-mb-0">
                    <strong className="tw-text-[#111] dark:tw-text-white">Lower than eBay</strong> — eBay charges sellers 13.25% + $0.30 per order. We only charge 10%. That's it.
                  </p>
                </div>
                <div className="tw-flex tw-items-start tw-gap-3 hiw-check-item">
                  <div className="tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-check tw-text-[10px]" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa] tw-leading-relaxed tw-mb-0">
                    <strong className="tw-text-[#111] dark:tw-text-white">Free shipping on $10+</strong> — Orders over $10 ship free. Orders $50+ include tracked shipping. Under $50, buyers can add tracking for $5.50. Orders under $10 have a flat $1 shipping fee.
                  </p>
                </div>
              </div>
            </div>

            {/* Fee example */}
            <div className={card + ' tw-p-8 md:tw-p-10 hiw-reveal hiw-fade-up'}>
              <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-8">
                Example: Sell a card for $8
              </h3>
              <div className="tw-max-w-sm tw-mx-auto">
                <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-[#eee] dark:tw-border-[#333] hiw-fee-row tw-px-2 tw-rounded">
                  <span className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa]">Listing price</span>
                  <span className="tw-text-sm tw-font-semibold tw-text-[#111] dark:tw-text-white">$8.00</span>
                </div>
                <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-[#eee] dark:tw-border-[#333] hiw-fee-row tw-px-2 tw-rounded">
                  <span className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa]">Shipping (flat fee)</span>
                  <span className="tw-text-sm tw-font-semibold tw-text-[#111] dark:tw-text-white">$1.00</span>
                </div>
                <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-[#eee] dark:tw-border-[#333] hiw-fee-row tw-px-2 tw-rounded">
                  <span className="tw-text-sm tw-text-[#555] dark:tw-text-[#aaa]">Platform fee (10%)</span>
                  <span className="tw-text-sm tw-font-semibold" style={{ color: '#e53935' }}>-$0.80</span>
                </div>
                <div className="tw-flex tw-justify-between tw-py-4 tw-px-2">
                  <span className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white">Your payout</span>
                  <span className="tw-text-lg tw-font-bold" style={{ color: green }}>$8.20</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw-divider tw-my-16"></div>

          {/* ==================== SHARE & EARN ==================== */}
          <div className="tw-mb-20 hiw-reveal hiw-scale-in">
            <div className={card + ' tw-p-8 md:tw-p-10 tw-text-center'}>
              <div className="tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-5 hiw-icon-pulse" style={{ backgroundColor: '#faf5e8' }}>
                <i className="fa fa-retweet tw-text-2xl" style={{ color: gold }}></i>
              </div>
              <h2 className="tw-text-xl md:tw-text-2xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-3">Share &amp; Earn</h2>
              <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-max-w-lg tw-mx-auto tw-mb-8 tw-leading-relaxed">
                See a listing you think a friend would love? Share it with your unique link. If they buy through your link, you earn a flat dollar commission set by the seller (minimum $1.00 per sale). Commissions are added to your balance and can be withdrawn anytime.
              </p>
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-mb-8">
                <div className="tw-text-center">
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-1" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-link" style={{ color: gold }}></i>
                  </div>
                  <p className="tw-text-[10px] tw-font-bold tw-tracking-wide tw-text-[#888] tw-mb-0">SHARE</p>
                </div>
                <span className="hiw-arrow-bounce"><i className="fa fa-arrow-right tw-text-[#ccc] dark:tw-text-[#555]"></i></span>
                <div className="tw-text-center">
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-1" style={{ backgroundColor: '#e3f2fd' }}>
                    <i className="fa fa-shopping-cart" style={{ color: '#1976d2' }}></i>
                  </div>
                  <p className="tw-text-[10px] tw-font-bold tw-tracking-wide tw-text-[#888] tw-mb-0">THEY BUY</p>
                </div>
                <span className="hiw-arrow-bounce" style={{ animationDelay: '0.3s' }}><i className="fa fa-arrow-right tw-text-[#ccc] dark:tw-text-[#555]"></i></span>
                <div className="tw-text-center">
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-1" style={{ backgroundColor: '#e8f5e9' }}>
                    <i className="fa fa-money" style={{ color: green }}></i>
                  </div>
                  <p className="tw-text-[10px] tw-font-bold tw-tracking-wide tw-text-[#888] tw-mb-0">YOU EARN</p>
                </div>
              </div>
              <Link to="/sharingmore" className="tw-text-sm tw-font-semibold tw-no-underline hover:tw-underline" style={{ color: gold }}>
                Learn more about Share &amp; Earn <i className="fa fa-arrow-right tw-ml-1"></i>
              </Link>
            </div>
          </div>

          <div className="hiw-divider tw-my-16"></div>

          {/* ==================== FEATURES ==================== */}
          <div className="tw-mb-20">
            <div className="hiw-reveal hiw-fade-up">
              <p className="tw-text-xs tw-font-bold tw-tracking-[0.2em] tw-uppercase tw-text-center tw-mb-2" style={{ color: gold }}>FEATURES</p>
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-text-center tw-mb-2">
                Built for the community
              </h2>
              <p className="tw-text-base tw-text-[#666] dark:tw-text-[#999] tw-text-center tw-mb-10">
                Everything you need to trade
              </p>
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-1'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-shopping-cart" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Shopping Cart</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Add cards from multiple sellers to your cart and check out in a single transaction. No more buying one card at a time.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-2'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-handshake-o" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Make an Offer</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      See a card priced too high? Make an offer on eligible listings. Sellers can accept, counter, or decline — all through built-in messaging.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-3'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-clone" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Bulk Listing</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Selling a collection? Use bulk listing to post multiple cards at once. Link to our card catalog for automatic images and details.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-4'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-star" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Seller Ratings</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Buyers leave ratings after each transaction. Build your reputation and become a trusted seller in the community.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-5'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#f3e8fd' }}>
                    <i className="fa fa-cc-stripe" style={{ color: '#6772e5' }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Stripe Payments</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Accept credit and debit cards through Stripe. Set up your connected account in under 2 minutes from your settings page.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-6'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#e3f2fd' }}>
                    <i className="fa fa-paypal" style={{ color: '#003087' }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">PayPal Payments</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Buyers can also pay with PayPal. Add your PayPal email in settings to receive payouts for PayPal orders.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-7'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-comments" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Direct Messaging</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Message any seller from their listing or your order history. Discuss card condition, negotiate prices, or coordinate shipping details.
                    </p>
                  </div>
                </div>
              </div>
              <div className={card + ' tw-p-6 hiw-feature-card hiw-reveal hiw-fade-up hiw-delay-8'}>
                <div className="tw-flex tw-items-start tw-gap-4">
                  <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#faf5e8' }}>
                    <i className="fa fa-line-chart" style={{ color: gold }}></i>
                  </div>
                  <div>
                    <h3 className="tw-text-base tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-2">Market Prices</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-leading-relaxed tw-mb-0">
                      Every card page shows real market data based on recent sales. Know exactly what a card is worth before you buy or set your price.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== CTA ==================== */}
          <div className="tw-text-center tw-py-16 hiw-reveal hiw-fade-up">
            <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-3">Ready to start?</h2>
            <p className="tw-text-base tw-text-[#666] dark:tw-text-[#999] tw-mb-8">
              Join the Primal TCG marketplace community today.
            </p>
            <button
              onClick={this.handleBrowse}
              className="tw-px-10 tw-py-4 tw-rounded-xl tw-font-bold tw-text-white tw-text-base tw-cursor-pointer tw-border-0 hiw-cta-btn"
              style={{ backgroundColor: gold }}
            >
              Browse the Market
            </button>
          </div>

          {/* Footer spacer */}
          <div className="tw-pb-4"></div>

        </div>

        {/* Scroll to top button */}
        {this.state.showScrollTop && (
          <button
            onClick={this.scrollToTop}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)', animation: 'hp-fadeUp 0.3s ease forwards' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </Layout>
    );
  }
}

export default DeepDescription;
